import React, { useState } from 'react';
import { useMutation } from '@apollo/client';
import { FormattedMessage } from 'react-intl';
import { Checkbox } from '../checkbox-or-radio-field';
import SearchInput from '../search-input';
import { mutations, queries } from '../../api';
import { filterDealBy } from '../selectDeals/helpers';
import { Permissions } from '../../shared/constants/permissions';
import { useTeamInfo } from '../../team-id-context';
import { getTeamByIdWithDeals } from '../../api/queries';
import { Slideout } from '../slideout';
import styles from './styles.module.scss';

interface Deal {
  id: string;
  title: string;
}

interface UserRowData {
  id: string;
  fullName?: string;
  firstName?: string;
  lastName?: string;
  email: string;
  isWhitelisted: boolean;
  dealIds: string[];
  permissions: any[];
  invitationId: string | null;
}

interface ManageDealAccessSlideoutProps {
  isOpen: boolean;
  onClose: () => void;
  userRowData: UserRowData;
  dealsForTeam: Deal[];
  whitelistPermissionId: string;
}

const ManageDealAccessSlideout = ({
  isOpen,
  onClose,
  userRowData,
  dealsForTeam,
  whitelistPermissionId,
}: ManageDealAccessSlideoutProps) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isWhitelisted, setIsWhitelisted] = useState(userRowData.isWhitelisted);
  const [localDealState, setLocalDealState] = useState<Record<string, boolean>>({});
  const { teamId } = useTeamInfo();

  // Update local state when userRowData changes
  React.useEffect(() => {
    setIsWhitelisted(userRowData.isWhitelisted);
    // Reset local deal state when userRowData changes
    setLocalDealState({});
  }, [userRowData.isWhitelisted, userRowData.dealIds]);

  // Function to determine if a deal checkbox should be checked
  const isDealChecked = (dealId: string) => {
    // If we have local state for this deal, use it
    if (dealId in localDealState) {
      return localDealState[dealId];
    }
    // Otherwise, use the default logic: whitelisted OR explicitly assigned
    return isWhitelisted || userRowData.dealIds.includes(dealId);
  };

  // Mutations - reuse existing patterns from SelectDeals
  const [updateUserPermissions] = useMutation(mutations.updateUserPermissions);
  const [removeTeamMemberFromDeal] = useMutation(mutations.removeTeamMemberFromDeal);
  const [inviteNewTeamMemberToDeal] = useMutation(mutations.inviteNewTeamMembersByEmail);
  const [updatePendingInvitation] = useMutation(mutations.updatePendingInvitation);

  const handleWhitelistToggle = async (checked: boolean) => {
    setIsLoading(true);
    try {
      // Update local state immediately for UI responsiveness
      setIsWhitelisted(checked);

      await updateUserPermissions({
        variables: {
          userId: userRowData.id,
          teamId,
          permissionUpdates: [
            {
              permissionKey: Permissions.Whitelist,
              allowed: checked,
            },
          ],
        },
        refetchQueries: [
          {
            query: queries.getDealsForTeam,
            variables: { teamId },
          },
          {
            query: getTeamByIdWithDeals,
            variables: { teamId },
          },
        ],
      });
    } catch (error) {
      console.error('Error updating whitelist permission:', error);
      // Revert local state if the mutation failed
      setIsWhitelisted(userRowData.isWhitelisted);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDealToggle = async (dealId: string, checked: boolean) => {
    // Update local state immediately for responsive UI
    setLocalDealState((prev) => ({ ...prev, [dealId]: checked }));

    // Determine the current actual state of deal access
    const userHasExplicitAccess = userRowData.dealIds.includes(dealId);
    const userIsWhitelisted = isWhitelisted;

    setIsLoading(true);
    try {
      const userPermissionsIds = userRowData.permissions.map(({ id }) => id);
      const userPermissions = [
        {
          email: userRowData.email,
          permissionIds: userPermissionsIds,
        },
      ];

      if (checked) {
        // User wants to grant access
        // Only add if they don't already have explicit access
        if (!userHasExplicitAccess) {
          if (userRowData.invitationId) {
            // Handle invitation user
            const currentSelectedDeals = userRowData.dealIds.filter((id) =>
              dealsForTeam.find((deal) => deal.id === id)
            );

            await updatePendingInvitation({
              variables: {
                id: userRowData.invitationId,
                dealIds: [...currentSelectedDeals, dealId],
                permissionIds: userPermissionsIds,
              },
            });
          } else {
            // Handle existing user
            await inviteNewTeamMemberToDeal({
              variables: {
                teamId,
                userId: userRowData.id,
                userPermissions,
                emails: [userRowData.email],
                defaultDeals: [dealId],
              },
              refetchQueries: [
                {
                  query: queries.getDealsForTeam,
                  variables: { teamId },
                },
                {
                  query: getTeamByIdWithDeals,
                  variables: { teamId },
                },
              ],
            });
          }
        }
        // If user is whitelisted and already has access, no action needed
      } else {
        // User wants to remove/deny access
        if (userRowData.invitationId) {
          // Handle invitation user - always update their deal list
          const updatedDealIds = userRowData.dealIds.filter((id) => id !== dealId);
          await updatePendingInvitation({
            variables: {
              id: userRowData.invitationId,
              dealIds: updatedDealIds,
              permissionIds: userPermissionsIds,
            },
          });
        } else {
          // Handle existing user
          // Only call removeTeamMemberFromDeal if they have explicit access
          if (userHasExplicitAccess) {
            await removeTeamMemberFromDeal({
              variables: {
                userId: userRowData.id,
                dealId: dealId,
              },
              refetchQueries: [
                {
                  query: getTeamByIdWithDeals,
                  variables: { teamId },
                },
                {
                  query: queries.getDealsForTeam,
                  variables: { teamId },
                },
              ],
            });
          }
          // Note: We can't create explicit exclusions for whitelisted users
          // The backend doesn't support a "deny list" concept
        }
      }
    } catch (error) {
      console.error('Error updating deal access:', error);
      // Revert local state on error
      setLocalDealState((prev) => {
        const newState = { ...prev };
        delete newState[dealId];
        return newState;
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Get user display name
  const displayName = userRowData.fullName || `${userRowData.firstName || ''} ${userRowData.lastName || ''}`.trim();

  // Filter deals based on search query
  const filteredDeals = dealsForTeam.filter((deal) => filterDealBy(deal, searchQuery.trim()));

  return (
    <Slideout isOpen={isOpen} onClose={onClose} title="Manage Deal Access" width="medium">
      {/* User Info */}
      <div className={styles.slideout__userInfo}>
        <h4 className={styles.slideout__userName}>{displayName || userRowData.email}</h4>
        <p className={styles.slideout__userEmail}>{userRowData.email}</p>
      </div>

      {/* Whitelist Toggle */}
      <div className={styles.slideout__whitelistSection}>
        <Checkbox
          id="whitelist-toggle"
          type="checkbox"
          checked={isWhitelisted}
          disabled={isLoading}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleWhitelistToggle(e.target.checked)}
          label={<FormattedMessage id="phrase.included-on-all-deals" defaultMessage="Included on All Deals" />}
        />
        <small className={styles.slideout__whitelistHelp}>
          <FormattedMessage
            id="phrase.whitelist-explanation"
            defaultMessage="User will automatically get access to all current and future deals"
          />
        </small>
      </div>

      {/* Separator */}
      <div className={styles.slideout__separator} />

      {/* Deals Section */}
      <div className={styles.slideout__dealsSection}>
        <h5 className={styles.slideout__dealsTitle}>
          <FormattedMessage id="title.select-individual-deals" defaultMessage="Select Individual Deals" />
        </h5>

        <SearchInput
          placeholder="Search deals..."
          value={searchQuery}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchQuery(e.target.value)}
          className={styles.slideout__searchInput}
        />

        <div className={styles.slideout__dealsList}>
          {filteredDeals.length > 0 ? (
            filteredDeals.map((deal) => (
              <div key={deal.id} className={styles.slideout__dealItem}>
                <Checkbox
                  id={`deal-${deal.id}`}
                  type="checkbox"
                  checked={isDealChecked(deal.id)}
                  disabled={isLoading}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleDealToggle(deal.id, e.target.checked)}
                  label={deal.title}
                />
              </div>
            ))
          ) : (
            <div className={styles.slideout__noDeals}>
              <FormattedMessage id="phrase.no-deals-found" defaultMessage="No deals found" />
            </div>
          )}
        </div>
      </div>
    </Slideout>
  );
};

export default ManageDealAccessSlideout;
